import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabase";
import { useDarkMode } from "../../contexts/DarkModeContext";
import "./Navbar.css";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [unreadBookings, setUnreadBookings] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const menuRef = useRef(null);

  useEffect(() => {
    fetchUserData();
    fetchNotifications();

    // Poll for notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const fetchUserData = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("full_name, application_submitted, reference_status")
        .eq("user_id", user.id)
        .single();

      setUserData({ ...profile, userId: user.id });
    }
  };

  const fetchNotifications = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      // Count unread booking requests (where user is the receiver/provider and haven't been viewed)
      const { count: bookingsCount } = await supabase
        .from("booking_requests")
        .select("*", { count: "exact", head: true })
        .eq("receiver_id", user.id)
        .eq("status", "pending")
        .eq("provider_viewed", false);

      setUnreadBookings(bookingsCount || 0);

      // Count unread messages (where user is the receiver and message is unread)
      const { count: messagesCount } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("receiver_id", user.id)
        .eq("read", false);

      setUnreadMessages(messagesCount || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleViewProfile = () => {
    if (userData?.userId) {
      navigate(`/profile/${userData.userId}`);
      setIsMenuOpen(false);
    }
  };

  const handleBrowseServices = () => {
    navigate("/explore");
    setIsMenuOpen(false);
  };

  const handleBookings = () => {
    navigate("/bookings");
    setIsMenuOpen(false);
  };

  const handleMessages = () => {
    navigate("/messages");
    setIsMenuOpen(false);
  };

  const handleSaved = () => {
    navigate("/saved");
    setIsMenuOpen(false);
  };

  const handleSettings = () => {
    navigate("/settings");
    setIsMenuOpen(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
    setIsMenuOpen(false);
  };

  return (
    <header
      style={{
        backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
        borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e5e5e5",
        padding: "1rem clamp(1rem, 4vw, 2rem)",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <nav
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <div
          onClick={() => navigate("/explore")}
          style={{
            fontSize: "24px",
            fontWeight: "600",
            color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
            letterSpacing: "-0.5px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          Collavo
        </div>

        {/* Primary nav links — visible on desktop */}
        <div className="navbar-primary-links">
          {[
            { label: "Explore", path: "/explore" },
            { label: "Saved", path: "/saved" },
            { label: "Invites", path: "/bookings", badge: unreadBookings },
            { label: "Messages", path: "/messages", badge: unreadMessages },
          ].map(({ label, path, badge }) => {
            const isActive = location.pathname === path;
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                style={{
                  position: "relative",
                  padding: "7px 12px",
                  fontSize: "14px",
                  fontWeight: isActive ? "600" : "400",
                  color: isActive
                    ? "#36454F"
                    : isDarkMode
                    ? "#a3a3a3"
                    : "#6B7280",
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "color 0.15s, background-color 0.15s",
                  textDecoration: isActive ? "underline" : "none",
                  textUnderlineOffset: "3px",
                  textDecorationThickness: "2px",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = isDarkMode ? "#e5e5e5" : "#111827";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = isDarkMode ? "#a3a3a3" : "#6B7280";
                }}
              >
                {label}
                {badge > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      width: "7px",
                      height: "7px",
                      backgroundColor: "#dc2626",
                      borderRadius: "50%",
                      border: isDarkMode ? "1.5px solid #1f1f1f" : "1.5px solid #fff",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {/* Apply Button - hidden once reference is confirmed and profile is live on Explore */}
          {userData && userData.reference_status !== "confirmed" && (
            <button
              onClick={() => navigate("/onboarding")}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#fff",
                backgroundColor: isDarkMode ? "#F97316" : "#1a1a1a",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = isDarkMode
                  ? "#ea6c0e"
                  : "#333")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = isDarkMode
                  ? "#F97316"
                  : "#1a1a1a")
              }
            >
              Apply
            </button>
          )}

          {/* Burger Menu */}
          <div style={{ position: "relative" }} ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{
                backgroundColor: "transparent",
                border: isDarkMode ? "1px solid #404040" : "1px solid #e5e5e5",
                borderRadius: "8px",
                padding: "10px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = isDarkMode
                  ? "#525252"
                  : "#a3a3a3";
                e.currentTarget.style.backgroundColor = isDarkMode
                  ? "#2a2a2a"
                  : "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDarkMode
                  ? "#404040"
                  : "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-label="Menu"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDarkMode ? "#e5e5e5" : "#1a1a1a"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>

            {/* Notification Badge on Hamburger */}
            {(unreadBookings > 0 || unreadMessages > 0) && (
              <span
                style={{
                  position: "absolute",
                  top: "6px",
                  right: "6px",
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#dc2626",
                  borderRadius: "50%",
                  border: "2px solid #fff",
                }}
              ></span>
            )}

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
                  border: isDarkMode
                    ? "1px solid #404040"
                    : "1px solid #e5e5e5",
                  borderRadius: "8px",
                  boxShadow: isDarkMode
                    ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                    : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  minWidth: "220px",
                  overflow: "hidden",
                  zIndex: 1001,
                }}
              >
                {userData?.full_name && (
                  <div
                    onClick={handleViewProfile}
                    style={{
                      padding: "12px 16px",
                      borderBottom: isDarkMode
                        ? "1px solid #404040"
                        : "1px solid #e5e5e5",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = isDarkMode
                        ? "#2a2a2a"
                        : "#fafafa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                        margin: 0,
                      }}
                    >
                      {userData.full_name}
                    </p>
                  </div>
                )}

                {location.pathname !== "/explore" && (
                  <button
                    onClick={handleBrowseServices}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                      backgroundColor: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = isDarkMode
                        ? "#2a2a2a"
                        : "#fafafa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    Explore
                  </button>
                )}

                <button
                  onClick={handleBookings}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    fontWeight: "400",
                    color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#fafafa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <span>Invites</span>
                  {unreadBookings > 0 && (
                    <span
                      style={{
                        minWidth: "20px",
                        height: "20px",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        borderRadius: "10px",
                        fontSize: "11px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 6px",
                      }}
                    >
                      {unreadBookings}
                    </span>
                  )}
                </button>

                <button
                  onClick={handleMessages}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    fontWeight: "400",
                    color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#fafafa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <span>Messages</span>
                  {unreadMessages > 0 && (
                    <span
                      style={{
                        minWidth: "20px",
                        height: "20px",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        borderRadius: "10px",
                        fontSize: "11px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 6px",
                      }}
                    >
                      {unreadMessages}
                    </span>
                  )}
                </button>

                <button
                  onClick={handleSaved}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    fontWeight: "400",
                    color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#fafafa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  Saved Profiles
                </button>

                <button
                  onClick={handleSettings}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    fontWeight: "400",
                    color: isDarkMode ? "#e5e5e5" : "#1a1a1a",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#fafafa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  Settings
                </button>

                <div
                  style={{
                    borderTop: isDarkMode
                      ? "1px solid #404040"
                      : "1px solid #e5e5e5",
                    marginTop: "4px",
                  }}
                />

                <button
                  onClick={handleSignOut}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    fontWeight: "400",
                    color: isDarkMode ? "#a3a3a3" : "#737373",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#fafafa";
                    e.currentTarget.style.color = isDarkMode
                      ? "#e5e5e5"
                      : "#525252";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = isDarkMode
                      ? "#a3a3a3"
                      : "#737373";
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
